import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { orderAPI } from '../services/api';
import { Order } from '../types';
import './OrderTracking.css';

const OrderTracking: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [orderNumber, setOrderNumber] = useState(searchParams.get('order') || '');
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleTrack = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderNumber.trim()) {
      setError(t('tracking.enterOrderNumber'));
      return;
    }

    setLoading(true);
    setError('');
    setOrder(null);

    try {
      const data = await orderAPI.trackOrder(orderNumber);
      setOrder(data);
    } catch (err) {
      setError(t('tracking.orderNotFound'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusSteps = () => {
    const steps = [
      { key: 'pending', label: t('tracking.status.pending') },
      { key: 'processing', label: t('tracking.status.processing') },
      { key: 'shipped', label: t('tracking.status.shipped') },
      { key: 'delivered', label: t('tracking.status.delivered') },
    ];

    const currentStatus = order?.status || 'pending';
    const currentIndex = steps.findIndex(s => s.key === currentStatus);

    return steps.map((step, index) => ({
      ...step,
      active: index <= currentIndex,
      current: step.key === currentStatus,
    }));
  };

  return (
    <div className="order-tracking-page">
      <div className="container">
        <h1>{t('tracking.title')}</h1>

        <div className="tracking-form">
          <form onSubmit={handleTrack}>
            <div className="form-group">
              <input
                type="text"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder={t('tracking.orderNumberPlaceholder')}
                className="order-input"
              />
              <button type="submit" className="track-btn" disabled={loading}>
                {loading ? t('common.loading') : t('tracking.trackButton')}
              </button>
            </div>
          </form>
          {error && <div className="error-message">{error}</div>}
        </div>

        {order && (
          <div className="tracking-results">
            <div className="order-header">
              <h2>{t('tracking.orderNumber')}: {order.order_number}</h2>
              <p className="order-date">
                {t('tracking.orderDate')}: {new Date(order.created_at!).toLocaleDateString()}
              </p>
            </div>

            <div className="status-timeline">
              {getStatusSteps().map((step, index) => (
                <div key={step.key} className={`timeline-step ${step.active ? 'active' : ''} ${step.current ? 'current' : ''}`}>
                  <div className="step-marker">
                    {step.active ? '✓' : index + 1}
                  </div>
                  <div className="step-label">{step.label}</div>
                  {index < 3 && <div className="step-line"></div>}
                </div>
              ))}
            </div>

            <div className="order-details-section">
              <div className="detail-card">
                <h3>{t('tracking.orderItems')}</h3>
                <div className="items-list">
                  {order.items.map((item, index) => (
                    <div key={index} className="item-row">
                      <span>{item.product_name}</span>
                      <span>× {item.quantity}</span>
                      <span>৳{item.subtotal?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="total-row">
                  <strong>{t('cart.total')}:</strong>
                  <strong>৳{order.total_amount?.toFixed(2)}</strong>
                </div>
              </div>

              <div className="detail-card">
                <h3>{t('tracking.shippingAddress')}</h3>
                <p>{order.shipping_address}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTracking;
